import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, CircleHelp, Copy, ExternalLink, Loader2, RefreshCw, Trash2 } from 'lucide-react';

import { DataSurface } from '@/components/platform/DataSurface';
import { StatusBadge } from '@/components/platform/StatusBadge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import customDomainsService from '@/lib/api/customDomains';
import salesAgentService from '@/lib/api/salesAgent';
import { getLiveDemoUrl, getStagingDemoUrl } from '@/lib/demoUrls';

const DOMAIN_PATTERN = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9-]{2,63}$/i;
const SLUG_PATTERN = /^[a-z0-9-]+$/;

function isValidHostname(value: string): boolean {
  const trimmed = value.trim().toLowerCase().replace(/\.$/, '');
  if (!trimmed) return false;
  if (trimmed.includes('://') || /[/?#:]/.test(trimmed)) return false;
  return DOMAIN_PATTERN.test(trimmed);
}

function normalizeDemoSlug(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '');
}

function getStatusBadge(status?: string | null) {
  switch (status) {
    case 'active':
      return <StatusBadge tone="success">Active</StatusBadge>;
    case 'failed':
      return <StatusBadge tone="danger">Failed</StatusBadge>;
    case 'pending':
      return <StatusBadge tone="warning">Pending</StatusBadge>;
    default:
      return <StatusBadge tone="pending">Not configured</StatusBadge>;
  }
}

interface CustomDomainSettingsCardProps {
  organizationId: string;
  isAdmin: boolean;
}

function InfoHint({ copy }: { copy: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground transition hover:text-foreground"
          aria-label="More information"
        >
          <CircleHelp className="h-4 w-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-[260px] text-xs leading-snug">
        {copy}
      </TooltipContent>
    </Tooltip>
  );
}

export function CustomDomainSettingsCard({
  organizationId,
  isAdmin,
}: CustomDomainSettingsCardProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [domainInput, setDomainInput] = useState('');
  const [slugInput, setSlugInput] = useState('');

  const customDomainQuery = useQuery({
    queryKey: ['custom-domain', organizationId],
    queryFn: async () => {
      const response = await customDomainsService.getCustomDomain(organizationId);
      if (response.success && response.data) return response.data;
      throw new Error(response.error || 'Failed to load custom domain');
    },
    enabled: Boolean(organizationId),
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.custom_domain?.status === 'pending' ? 5000 : false,
  });
  const salesAgentQuery = useQuery({
    queryKey: ['sales-agent', organizationId],
    queryFn: async () => {
      const response = await salesAgentService.getSalesAgent(organizationId);
      if (response.success && response.data) return response.data;
      if (response.error === 'Sales agent not found for this organization') return null;
      throw new Error(response.error || 'Failed to load sales agent');
    },
    enabled: Boolean(organizationId),
    retry: false,
  });

  const currentDomain = customDomainQuery.data?.custom_domain ?? null;
  const dnsTarget = customDomainQuery.data?.dns_target ?? '';
  const liveUrl = currentDomain?.status === 'active' ? `https://${currentDomain.domain}/` : null;
  const salesAgent = salesAgentQuery.data ?? null;
  const liveSlugUrl = getLiveDemoUrl(salesAgent?.demo_slug, null);
  const stagingSlugUrl = getStagingDemoUrl(salesAgent?.staging_slug);

  useEffect(() => {
    if (currentDomain?.domain) {
      setDomainInput(currentDomain.domain);
      return;
    }
    if (!customDomainQuery.data?.custom_domain) {
      setDomainInput('');
    }
  }, [currentDomain?.domain, customDomainQuery.data?.custom_domain]);
  useEffect(() => {
    setSlugInput(salesAgent?.demo_slug ?? '');
  }, [salesAgent?.demo_slug]);

  const normalizedInput = useMemo(
    () => domainInput.trim().toLowerCase().replace(/\.$/, ''),
    [domainInput],
  );
  const normalizedSlugInput = useMemo(() => normalizeDemoSlug(slugInput), [slugInput]);
  const setupHostname = currentDomain?.domain || 'demo.acme.com';
  const inputLooksValid = normalizedInput.length > 0 && isValidHostname(normalizedInput);
  const slugInputLooksValid = normalizedSlugInput.length > 0 && SLUG_PATTERN.test(normalizedSlugInput);
  const createDisabled =
    !isAdmin ||
    !inputLooksValid ||
    customDomainQuery.isLoading ||
    Boolean(currentDomain);
  const slugChanged = normalizedSlugInput !== (salesAgent?.demo_slug ?? '');
  const slugSaveDisabled =
    !isAdmin ||
    !salesAgent ||
    !slugInputLooksValid ||
    !slugChanged;

  const createMutation = useMutation({
    mutationFn: async (domain: string) => {
      const response = await customDomainsService.createCustomDomain(organizationId, { domain });
      if (response.success && response.data) return response.data;
      throw new Error(response.error || 'Failed to create custom domain');
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['custom-domain', organizationId], data);
      toast({
        title: 'Custom domain created',
        description: 'Add the CNAME record below and keep this page open while validation completes.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Could not create custom domain',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const response = await customDomainsService.deleteCustomDomain(organizationId);
      if (response.success && response.data) return response.data;
      throw new Error(response.error || 'Failed to delete custom domain');
    },
    onSuccess: () => {
      queryClient.setQueryData(['custom-domain', organizationId], {
        custom_domain: null,
        dns_target: dnsTarget,
      });
      setDomainInput('');
      toast({
        title: 'Custom domain removed',
        description: 'Preview links now fall back to the default Hobbes demo URL.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Could not delete custom domain',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
  const updateSlugMutation = useMutation({
    mutationFn: async (demoSlug: string) => {
      const response = await salesAgentService.updateSalesAgent(organizationId, { demo_slug: demoSlug });
      if (response.success && response.data) return response.data;
      throw new Error(response.error || 'Failed to update demo URL');
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['sales-agent', organizationId], data);
      toast({
        title: 'Demo URL updated',
        description: `Your production URL is now ${getLiveDemoUrl(data.demo_slug, null)}`,
      });
    },
    onError: (error: Error) => {
      const message = error.message || 'Failed to update demo URL';
      toast({
        title: message.toLowerCase().includes('already in use') ? 'URL slug already in use' : 'Could not update demo URL',
        description: message,
        variant: 'destructive',
      });
    },
  });

  const handleCreate = async () => {
    if (!inputLooksValid) {
      toast({
        title: 'Enter a valid hostname',
        description: 'Use a full hostname like demo.acme.com without http:// or any path.',
        variant: 'destructive',
      });
      return;
    }

    await createMutation.mutateAsync(normalizedInput);
  };
  const handleSlugSave = async () => {
    if (!salesAgent) return;
    if (!slugInputLooksValid) {
      toast({
        title: 'Enter a valid slug',
        description: 'Use letters, numbers, or hyphens only. Spaces and symbols will be converted to hyphens.',
        variant: 'destructive',
      });
      return;
    }

    await updateSlugMutation.mutateAsync(normalizedSlugInput);
  };

  const handleRefresh = async () => {
    try {
      const response = await customDomainsService.getCustomDomain(organizationId, true);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to refresh custom domain');
      }
      queryClient.setQueryData(['custom-domain', organizationId], response.data);
      toast({
        title: 'Domain status refreshed',
      });
    } catch (error) {
      toast({
        title: 'Could not refresh custom domain',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async () => {
    if (!currentDomain || !isAdmin) return;
    const confirmed = confirm(
      `Delete the custom domain ${currentDomain.domain}? Traffic will fall back to the Hobbes demo URL.`,
    );
    if (!confirmed) return;
    await deleteMutation.mutateAsync();
  };

  const copyToClipboard = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: `${label} copied` });
    } catch {
      toast({
        title: `Could not copy ${label.toLowerCase()}`,
        variant: 'destructive',
      });
    }
  };

  return (
    <DataSurface>
      <div className="p-4 sm:p-5">
        {customDomainQuery.isLoading || salesAgentQuery.isLoading ? (
          <div className="flex min-h-[160px] items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading domain settings…
          </div>
        ) : (
          <>
            {customDomainQuery.error ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Custom domains unavailable</AlertTitle>
                <AlertDescription>
                  {customDomainQuery.error instanceof Error
                    ? customDomainQuery.error.message === 'Not Found'
                      ? 'This environment is not serving the custom-domain API yet. Once the backend is deployed, you will be able to save a hostname here and see the DNS target for your DNS provider.'
                      : customDomainQuery.error.message
                    : 'We could not load the custom domain settings.'}
                </AlertDescription>
              </Alert>
            ) : null}
            {salesAgentQuery.error ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Demo URL settings unavailable</AlertTitle>
                <AlertDescription>
                  {salesAgentQuery.error instanceof Error
                    ? salesAgentQuery.error.message
                    : 'We could not load the demo URL settings.'}
                </AlertDescription>
              </Alert>
            ) : null}
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(300px,0.85fr)]">
              <div className="divide-y divide-border/70">
                <section className="space-y-4 pb-6">
                  <div className="space-y-2">
                    <p className="text-xs font-medium uppercase text-muted-foreground">Demo URLs</p>
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-semibold tracking-tight">Production URL</h3>
                      <InfoHint copy="This slug determines your live demo URL where visitors interact with your agent. The staging URL is generated automatically for internal testing." />
                    </div>
                  </div>

                  {salesAgent ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between gap-3">
                        <Label htmlFor="live-demo-slug-input">URL slug</Label>
                        <StatusBadge tone="info" showDot={false}>Production</StatusBadge>
                      </div>
                      <div className="flex flex-col gap-3 sm:flex-row">
                        <Input
                          id="live-demo-slug-input"
                          value={slugInput}
                          onChange={(event) => setSlugInput(event.target.value)}
                          placeholder="acme-demo"
                          disabled={!isAdmin || updateSlugMutation.isPending}
                          className="h-9"
                        />
                        <Button
                          type="button"
                          onClick={handleSlugSave}
                          disabled={slugSaveDisabled || updateSlugMutation.isPending}
                          className="h-9 rounded-md"
                        >
                          {updateSlugMutation.isPending ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Saving…
                            </>
                          ) : (
                            'Save'
                          )}
                        </Button>
                      </div>
                      {slugInput && !normalizedSlugInput ? (
                        <p className="text-sm text-destructive">
                          Enter at least one letter or number. We&apos;ll convert spaces and symbols to hyphens.
                        </p>
                      ) : null}
                      {normalizedSlugInput && normalizedSlugInput !== slugInput.trim().toLowerCase() ? (
                        <p className="text-sm text-muted-foreground">
                          We&apos;ll save this as <span className="font-mono">{normalizedSlugInput}</span>.
                        </p>
                      ) : null}
                      {!normalizedSlugInput ? (
                        <p className="text-sm text-muted-foreground">
                          Set a slug to create your production demo URL.
                        </p>
                      ) : null}
                      {liveSlugUrl ? (
                        <div className="rounded-lg border border-border/80 bg-background p-3.5">
                          <div className="space-y-3">
                            <div className="space-y-1">
                              <p className="text-xs font-medium text-muted-foreground">Production URL</p>
                              <p className="font-mono text-sm text-foreground">{liveSlugUrl}</p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-xs font-medium text-muted-foreground">Staging URL</p>
                              <p className="font-mono text-sm text-muted-foreground">
                                {stagingSlugUrl || 'A staging URL will appear here once you create a staging version of your agent.'}
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : null}
                      {!liveSlugUrl ? (
                        <div className="rounded-lg border border-border/80 bg-background p-3.5">
                          <div className="space-y-3">
                            <div className="space-y-1">
                              <p className="text-xs font-medium text-muted-foreground">Production URL</p>
                              <p className="font-mono text-sm text-muted-foreground">
                                Set a URL slug above to generate your production demo link.
                              </p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-xs font-medium text-muted-foreground">Staging URL</p>
                              <p className="font-mono text-sm text-muted-foreground">
                                {stagingSlugUrl || 'A staging URL will appear here once you create a staging version of your agent.'}
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : null}
                      {!isAdmin ? (
                        <p className="text-sm text-muted-foreground">
                          Only organization admins can update the demo URL.
                        </p>
                      ) : null}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-border/80 p-3.5 text-sm text-muted-foreground">
                      Create a production agent to set and manage its demo route here.
                    </div>
                  )}
                </section>

                <section className="space-y-4 pt-6">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs font-medium uppercase text-muted-foreground">Custom Domain</p>
                        <div className="flex items-center gap-2">
                          <h3 className="text-base font-semibold tracking-tight">Custom domain</h3>
                          <InfoHint copy="Your custom domain replaces the default Hobbes URL for your production demo page. Staging and embedded demos continue to use Hobbes URLs." />
                        </div>
                      </div>
                      {getStatusBadge(currentDomain?.status)}
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 sm:flex-row">
                    <Input
                      id="custom-domain-input"
                      value={domainInput}
                      onChange={(event) => setDomainInput(event.target.value)}
                      placeholder="demo.acme.com"
                      disabled={Boolean(currentDomain) || !isAdmin || createMutation.isPending}
                      className="h-9"
                    />
                    <Button
                      type="button"
                      onClick={handleCreate}
                      disabled={createDisabled || createMutation.isPending}
                      className="h-9 rounded-md"
                    >
                      {createMutation.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving…
                        </>
                      ) : (
                        'Save Domain'
                      )}
                    </Button>
                  </div>
                  {!currentDomain && !isAdmin ? (
                    <p className="text-sm text-muted-foreground">
                      Only organization admins can configure a custom domain.
                    </p>
                  ) : null}
                  {!currentDomain && isAdmin ? (
                    <p className="text-sm text-muted-foreground">
                      Enter the exact hostname you will configure, like <span className="font-mono">demo.acme.com</span>. Do not include <span className="font-mono">https://</span> or a path.
                    </p>
                  ) : null}
                  {!currentDomain && normalizedInput && !inputLooksValid ? (
                    <p className="text-sm text-destructive">
                      Enter a hostname only. Do not include <span className="font-mono">https://</span>, ports, or any path.
                    </p>
                  ) : null}

                  {currentDomain ? (
                    <div className="space-y-4 rounded-lg border border-border/80 bg-background p-3.5">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="space-y-1">
                          <p className="font-mono text-sm text-foreground">{currentDomain.domain}</p>
                          <p className="text-sm text-muted-foreground">
                            Cloudflare status: <span className="font-mono">{currentDomain.cloudflare_status || 'unknown'}</span>
                          </p>
                          {currentDomain.failure_reason ? (
                            <p className="text-sm text-destructive">{currentDomain.failure_reason}</p>
                          ) : null}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 rounded-md"
                            onClick={handleRefresh}
                          >
                            <RefreshCw className="mr-2 h-4 w-4" />
                            Refresh
                          </Button>
                          {liveUrl ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-8 rounded-md"
                              onClick={() => window.open(liveUrl, '_blank')}
                            >
                              <ExternalLink className="mr-2 h-4 w-4" />
                              Open
                            </Button>
                          ) : null}
                          {isAdmin ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-8 rounded-md text-destructive hover:text-destructive"
                              onClick={handleDelete}
                              disabled={deleteMutation.isPending}
                            >
                              {deleteMutation.isPending ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="mr-2 h-4 w-4" />
                              )}
                              Delete
                            </Button>
                          ) : null}
                        </div>
                      </div>

                      {currentDomain.status === 'active' ? (
                        <Alert>
                          <CheckCircle2 className="h-4 w-4" />
                          <AlertTitle>Domain is active</AlertTitle>
                          <AlertDescription>
                            Your live full-page demo now resolves at <span className="font-mono">{liveUrl}</span>.
                          </AlertDescription>
                        </Alert>
                      ) : (
                        <Alert>
                          <RefreshCw className="h-4 w-4" />
                          <AlertTitle>Waiting for DNS and certificate validation</AlertTitle>
                          <AlertDescription>
                            Add the CNAME below and keep this page open. We poll Cloudflare every 5 seconds while the domain is pending.
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                  ) : null}
                </section>
              </div>

              <div className="space-y-4">
                <section className="rounded-lg border border-border/80 bg-background p-4">
                  <div className="space-y-1">
                    <p className="text-xs font-medium uppercase text-muted-foreground">Setup</p>
                    <h3 className="text-base font-semibold tracking-tight">Custom domain flow</h3>
                  </div>
                  <div className="mt-4 space-y-3.5">
                    {[
                      `Choose a hostname like ${setupHostname}.`,
                      'Save it here so Hobbes can register it with Cloudflare.',
                      'Create a CNAME in your DNS provider pointing that hostname to the Hobbes target.',
                      'Keep this page open while Cloudflare validates and issues the certificate.',
                    ].map((step, index) => (
                      <div key={step} className="flex gap-3">
                        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-border/80 bg-card text-xs font-medium text-muted-foreground">
                          {index + 1}
                        </div>
                        <p className="pt-0.5 text-sm text-muted-foreground">{step}</p>
                      </div>
                    ))}
                  </div>
                </section>

                {dnsTarget ? (
                  <section className="rounded-lg border border-border/80 bg-background p-4">
                    <div className="space-y-1">
                      <p className="text-xs font-medium uppercase text-muted-foreground">DNS Record</p>
                      <h3 className="text-base font-semibold tracking-tight">What to add in DNS</h3>
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground">
                      In your DNS provider, create a <span className="font-mono">CNAME</span> for <span className="font-mono">{currentDomain?.domain || setupHostname}</span> that points to the target below.
                    </p>
                    <div className="mt-4 grid gap-3 sm:grid-cols-2">
                      <div className="rounded-md border border-border/80 bg-card p-3">
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <p className="text-xs font-medium text-muted-foreground">Hostname</p>
                          {currentDomain?.domain ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-7 rounded-md px-2"
                              onClick={() => copyToClipboard(currentDomain.domain, 'Hostname')}
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </Button>
                          ) : null}
                        </div>
                        <p className="font-mono text-sm text-foreground">{currentDomain?.domain || setupHostname}</p>
                      </div>
                      <div className="rounded-md border border-border/80 bg-card p-3">
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <p className="text-xs font-medium text-muted-foreground">Target</p>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 rounded-md px-2"
                            onClick={() => copyToClipboard(dnsTarget, 'Target')}
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        <p className="font-mono text-sm text-foreground">{dnsTarget}</p>
                      </div>
                    </div>
                  </section>
                ) : null}
              </div>
            </div>
          </>
        )}
      </div>
    </DataSurface>
  );
}
